import { ChartDateRange, RevenueExpenseData, ExpenseBreakdownData } from '../hooks/useChartsData';

// Interface pour les paramètres de requête des graphiques
export interface ChartDataRequest {
  dateRange: ChartDateRange;
  organizationId?: string;
  categories?: string[];
}

// Interface pour la réponse des données de revenus et charges
export interface ChartDataResponse {
  revenueExpenseData: RevenueExpenseData[];
  expenseBreakdownData: ExpenseBreakdownData[];
  totalRevenue: number;
  totalExpenses: number;
  period: string;
}

// Service API pour les données de graphiques
export class ChartsApiService {
  private static baseUrl = '/api/charts';

  // Simuler un délai d'API
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Récupérer les données de revenus et charges
  static async getRevenueExpenseData(request: ChartDataRequest): Promise<RevenueExpenseData[]> {
    await this.delay(300); // Simuler la latence réseau

    try {
      // En production, ceci ferait un appel API réel
      // const response = await fetch(`${this.baseUrl}/revenue-expense`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(request)
      // });
      // return await response.json();

      // Pour l'instant, retourner des données mockées
      return this.generateMockRevenueExpenseData(request.dateRange);
    } catch (error) {
      console.error('Erreur lors de la récupération des données CA/Charges:', error);
      throw new Error('Impossible de récupérer les données de revenus et charges');
    }
  }

  // Récupérer les données de répartition des charges
  static async getExpenseBreakdownData(request: ChartDataRequest): Promise<ExpenseBreakdownData[]> {
    await this.delay(250);

    try {
      // En production:
      // const response = await fetch(`${this.baseUrl}/expense-breakdown`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(request)
      // });
      // return await response.json();

      return this.generateMockExpenseBreakdown();
    } catch (error) {
      console.error('Erreur lors de la récupération de la répartition des charges:', error);
      throw new Error('Impossible de récupérer la répartition des charges');
    }
  }

  // Récupérer toutes les données de graphiques en une seule requête
  static async getAllChartData(request: ChartDataRequest): Promise<ChartDataResponse> {
    await this.delay(400);

    try {
      // En production:
      // const response = await fetch(`${this.baseUrl}/all`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(request)
      // });
      // return await response.json();

      const [revenueExpenseData, expenseBreakdownData] = await Promise.all([
        this.getRevenueExpenseData(request),
        this.getExpenseBreakdownData(request)
      ]);

      const totalRevenue = revenueExpenseData.reduce((sum, item) => sum + item.revenue, 0);
      const totalExpenses = revenueExpenseData.reduce((sum, item) => sum + item.expenses, 0);

      return {
        revenueExpenseData,
        expenseBreakdownData,
        totalRevenue,
        totalExpenses,
        period: this.formatPeriod(request.dateRange)
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des données complètes:', error);
      throw new Error('Impossible de récupérer les données des graphiques');
    }
  }

  // Utilitaires privés pour les données mockées
  private static generateMockRevenueExpenseData(dateRange: ChartDateRange): RevenueExpenseData[] {
    const data: RevenueExpenseData[] = [];
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    const currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    
    while (currentDate <= endDate) {
      const monthIndex = currentDate.getMonth();
      const year = currentDate.getFullYear();
      
      // Variations saisonnières et tendances
      const seasonalFactor = 1 + 0.3 * Math.sin((monthIndex / 12) * Math.PI * 2);
      const trendFactor = 1 + (year - 2023) * 0.05; // Croissance annuelle de 5%
      
      const baseRevenue = 15000000 * seasonalFactor * trendFactor;
      const baseExpenses = 8000000 * seasonalFactor * trendFactor;
      
      // Ajouter de la variabilité réaliste
      const revenueVariation = (Math.random() - 0.5) * 0.2; // ±20%
      const expenseVariation = (Math.random() - 0.5) * 0.15; // ±15%
      
      const revenue = baseRevenue * (1 + revenueVariation);
      const expenses = baseExpenses * (1 + expenseVariation);
      
      data.push({
        date: currentDate.toISOString().split('T')[0],
        revenue: Math.max(0, Math.round(revenue)),
        expenses: Math.max(0, Math.round(expenses)),
        month: currentDate.toLocaleDateString('fr-FR', { month: 'short' }),
        year
      });
      
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return data;
  }

  private static generateMockExpenseBreakdown(): ExpenseBreakdownData[] {
    const categories = [
      { name: 'Salaires & Charges sociales', base: 4500000, color: '#ef4444' },
      { name: 'Loyers & Charges locatives', base: 1200000, color: '#f97316' },
      { name: 'Achats de marchandises', base: 2800000, color: '#eab308' },
      { name: 'Services externes', base: 800000, color: '#22c55e' },
      { name: 'Frais financiers', base: 300000, color: '#3b82f6' },
      { name: 'Impôts & Taxes', base: 600000, color: '#8b5cf6' },
      { name: 'Autres charges', base: 400000, color: '#6b7280' }
    ];

    const data = categories.map(category => {
      const variation = (Math.random() - 0.5) * 0.25; // ±25%
      const value = category.base * (1 + variation);
      
      return {
        name: category.name,
        value: Math.max(0, Math.round(value)),
        color: category.color
      };
    });

    // Calculer les pourcentages
    const total = data.reduce((sum, item) => sum + item.value, 0);
    return data.map(item => ({
      ...item,
      percentage: (item.value / total) * 100
    }));
  }

  private static formatPeriod(dateRange: ChartDateRange): string {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    
    if (dateRange.period === 'custom') {
      return `${start.toLocaleDateString('fr-FR')} - ${end.toLocaleDateString('fr-FR')}`;
    }
    
    const periodLabels = {
      'last7days': '7 derniers jours',
      'last30days': '30 derniers jours',
      'last3months': '3 derniers mois',
      'last6months': '6 derniers mois',
      'last12months': '12 derniers mois',
      'thisYear': 'Cette année',
      'lastYear': 'Année précédente'
    };
    
    return periodLabels[dateRange.period] || 'Période personnalisée';
  }

  // Méthodes pour la gestion du cache (optionnel)
  private static cache = new Map<string, { data: any; timestamp: number }>();
  private static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private static getCacheKey(request: ChartDataRequest): string {
    return JSON.stringify(request);
  }

  private static getCachedData<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  private static setCachedData(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  // Vider le cache (utile lors des changements de données)
  static clearCache(): void {
    this.cache.clear();
  }
}
